<template>
  <div>
    <div v-if="!loading">
      <page-header>
        <template #title>
          <h1 class="page-title" v-text="pageTitle"></h1>
        </template>
      </page-header>
      <div class="card mb-3">
        <div class="card-body">
          <div class="row">
            <div class="col-lg-6">
              <vehicle-lookup
                v-on:vrm="setRegistration"
                v-on:vehicle="setVehicle"
                :stacked="false"
                label="VRN"
                :show-error="form.errors.has('registration')"
                helper="Please enter the vehicle registration number and click the 'Find Vehicle' button"
                :registration="form.registration"
              ></vehicle-lookup>

              <selectlist
                :options="types"
                v-model="form.type_id"
                @input="setType"
                label="Vehicle Type"
                placeholder="Please Select"
                helper="Please select the vehicle type"
                name="type"
              ></selectlist>

              <selectlist
                v-if="!!showDriver"
                v-model="form.driver_id"
                :options="drivers"
                label="Driver"
                name="driver"
                label-name="full_name"
                helper="Please select the driver that is assigned to this vehicle"
              ></selectlist>

              <selectlist
                :options="groups"
                v-model="form.group_id"
                label="Vehicle Group"
                label-name="name"
                placeholder="Please Select"
                helper="Please select the group for this vehicle"
                :show-error="form.errors.has('group')"
                name="group"
              ></selectlist>

              <selectlist
                :options="makes"
                v-model="form.make_id"
                label="Vehicle Make"
                placeholder="Please Select"
                helper="Please select the make for this vehicle"
                :show-error="form.errors.has('make')"
                name="make"
              ></selectlist>

              <inputbox
                v-model="form.model"
                name="model"
                label="Vehicle Model"
                helper="Please enter the model for this vehicle"
                :show-error="form.errors.has('model')"
              ></inputbox>

              <selectlist
                :options="transmissions"
                v-model="form.transmission_type_id"
                name="transmission"
                placeholder="Please Select"
                helper="Please select the transmission type for this vehicle"
                label="Vehicle Transmission"
              ></selectlist>

              <inputbox
                v-model="form.colour"
                name="vehicle_colour"
                label="Vehicle Colour"
                helper="Please enter the colour of this vehicle"
              ></inputbox>

              <inputbox
                v-model="form.engine_capacity"
                name="engine_capacity"
                label="Engine Capacity"
                helper="Please enter the engine capacity for this vehicle"
              ></inputbox>

              <inputbox
                v-model="form.vin"
                name="vin"
                label="VIN"
                helper="Please enter the VIN for this vehicle"
              ></inputbox>

              <input-block
                v-model="form.comments"
                name="comments"
                label="Comments"
                helper="Add any general comments about the vehicle here"
              ></input-block>

              <vehicle-extras
                v-model="form.extras"
                :extras="extras"
              ></vehicle-extras>
            </div>

            <div class="col-lg-6">
              <selectlist
                :options="hireSites"
                v-model="form.hire_site_id"
                name="hire_site_id"
                placeholder="Please Select"
                helper="Please select the hire site for this vehicle"
                label="Hire Site"
                label-name="name"
              ></selectlist>

              <selectlist
                v-model="form.status_id"
                name="status_id"
                label="Status"
                helper="Please select the current status of the vehicle"
                :options="statuses"
                v-if="identifier"
              ></selectlist>

              <datepicker-input
                name="registered_at"
                label="Registration Date"
                helper="Please enter the date that this vehicle was registered"
                v-model="form.registered_at"
              ></datepicker-input>

              <div class="row">
                <div class="col-md-8 offset-md-4">
                  <div class="alert alert-info">
                    <p class="mb-0">
                      To get the tax and mot expiry dates, please go to the
                      following page:
                      <a
                        href="https://www.vehicleenquiry.service.gov.uk/"
                        target="_blank"
                        >https://www.vehicleenquiry.service.gov.uk/</a
                      >
                    </p>
                  </div>
                </div>
              </div>

              <datepicker-input
                name="tax_expiry"
                label="Tax Expiry"
                helper="Please enter the date the tax expires on this vehicle"
                v-model="form.tax_expires_at"
              ></datepicker-input>

              <datepicker-input
                name="mot_expiry"
                label="MOT Expiry"
                helper="Please enter the date that the MOT expires on this vehicle"
                v-model="form.mot_expires_at"
              ></datepicker-input>

              <datepicker-input
                name="lease_start_date"
                label="Lease Start Date"
                helper="Please enter the date that the lease starts on this vehicle"
                v-model="form.lease_start_date"
              ></datepicker-input>

              <datepicker-input
                name="lease_end_date"
                label="Lease End Date"
                helper="Please enter the date that the lease ends on this vehicle"
                v-model="form.lease_end_date"
              ></datepicker-input>

              <currency-input
                label="Cost Per Month"
                name="cost_per_month"
                helper="Please enter the cost per month of this vehicle"
                v-model="form.cost_per_month"
              >
                <div slot="leftAddon">&pound;</div>
              </currency-input>

              <currency-input
                label="P11D Value"
                name="p11d_value"
                helper="Please enter the P11D value for this vehicle"
                v-model="form.p11d_value"
              >
                <div slot="leftAddon">&pound;</div>
              </currency-input>
            </div>
          </div>
          <hr />
          <div class="row" v-if="!!isDefleeted()">
            <div class="col-md-6">
              <fieldset>
                <legend>Defleet Reason</legend>
                <selectlist
                  :options="defleetReasons"
                  v-model="form.defleet_reason_id"
                  name="defleet_reason"
                  placeholder="Defleet Reason"
                  helper="Please select the Defleet reason"
                  label="Defleet Reason"
                ></selectlist>

                <inputbox
                  v-model="form.total_loss_category"
                  name="total_loss_category"
                  label="Total Loss Category"
                  helper="Please enter if this in total loss category"
                ></inputbox>

                <input-block
                  v-model="form.reason"
                  name="reason"
                  label="Reason"
                  helper="Please enter reason"
                ></input-block>
              </fieldset>
            </div>

            <div class="col-md-6">
              <fieldset>
                <legend>Other Details</legend>
                <toggleswitch
                  :labels="true"
                  name="removed_from_dart_congestion"
                  label="Removed from Dart/Congestion"
                  v-model="form.removed_from_dart_congestion"
                ></toggleswitch>

                <toggleswitch
                  :labels="true"
                  name="removed_from_mid"
                  label="Removed from MID"
                  v-model="form.removed_from_mid"
                ></toggleswitch>

                <toggleswitch
                  :labels="true"
                  name="v5"
                  label="V5"
                  v-model="form.v5"
                ></toggleswitch>

                <toggleswitch
                  :labels="true"
                  name="spare_key"
                  label="Spare Key"
                  v-model="form.spare_key"
                ></toggleswitch>

                <toggleswitch
                  :labels="true"
                  name="sorn"
                  label="SORN"
                  v-model="form.sorn"
                ></toggleswitch>

                <toggleswitch
                  v-if="!!isTheft()"
                  :labels="true"
                  name="un_recovered_stolen"
                  label="UnRecovered stolen"
                  v-model="form.un_recovered_stolen"
                ></toggleswitch>
              </fieldset>
            </div>
          </div>
          <div class="row" v-if="!!isDefleeted()">
            <div class="col-md-6">
              <fieldset>
                <legend>Dates</legend>

                <datepicker-input
                  name="removed_from_dart_congestion_at"
                  label="Removed From Dart/Congestion Date"
                  helper="Please enter the date the vehicle removed from Dart/Congestion"
                  v-model="form.removed_from_dart_congestion_at"
                ></datepicker-input>

                <datepicker-input
                  name="sorn_at"
                  label="SORN Date"
                  helper="Please enter the SORN date"
                  v-model="form.sorn_at"
                ></datepicker-input>

                <datepicker-input
                  name="defleet_arranged_at"
                  label="Date Defleet Arranged"
                  helper="Please enter the defleet arranged date"
                  v-model="form.defleet_arranged_at"
                ></datepicker-input>

                <!--                <datepicker-input-->
                <!--                  name="collection_date"-->
                <!--                  label="Collection Date"-->
                <!--                  helper="Please enter the collection date"-->
                <!--                  v-model="form.collection_date"-->
                <!--                ></datepicker-input>-->

                <datepicker-input
                  name="off_mid_date"
                  label="Off MID date"
                  helper="Please enter the off MID date"
                  v-model="form.off_mid_date"
                ></datepicker-input>
              </fieldset>
            </div>
            <div class="col-md-6" v-if="!isTheft()">
              <fieldset>
                <legend>Disposal Info</legend>
                <inputbox
                  v-model="form.customer"
                  name="customer"
                  label="Customer"
                  helper="Please enter customer name"
                ></inputbox>

                <currency-input
                  label="Sale Price"
                  name="sale_price"
                  helper="Please enter the sale price"
                  v-model="form.sale_price"
                >
                  <div slot="leftAddon">&pound;</div>
                </currency-input>

                <datepicker-input
                  name="sale_date"
                  label="Sale Date"
                  helper="Please enter the sale date"
                  v-model="form.sale_date"
                ></datepicker-input>

                <datepicker-input
                  name="payment_date"
                  label="Payment Date"
                  helper="Please enter the payment date"
                  v-model="form.payment_date"
                ></datepicker-input>

                <inputbox
                  v-model="form.invoice_number"
                  name="invoice_number"
                  label="Invoice Number"
                  helper="Please enter invoice number"
                ></inputbox>

                <toggleswitch
                  :labels="true"
                  name="v5_processed"
                  label="V5 Processed?"
                  v-model="form.v5_processed"
                ></toggleswitch>

                <datepicker-input
                  name="collected_at"
                  label="Collected Date"
                  helper="Please enter the collected date"
                  v-model="form.collected_at"
                ></datepicker-input>
              </fieldset>
            </div>
          </div>
        </div>
        <div class="card-footer text-right">
          <button
            class="btn btn-primary btn-raised"
            name="save"
            v-on:click.prevent="submit"
          >
            <i class="icon icon-save"></i> Save
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>
  </div>
</template>

<script>
import VehicleLookup from '../../Generic/VehicleLookup.vue';
import CurrencyInput from '@/components/Generic/Form/CurrencyInput';
import VehicleExtras from '@/components/Modules/Fleet/Form/VehicleExtras';
import PageHeader from './../Shared/PageHeader.vue';

export default {
  props: {
    identifier: {
      type: String
    }
  },

  components: {
    CurrencyInput,
    VehicleLookup,
    VehicleExtras,
    PageHeader
  },

  data() {
    return {
      currentView: 'details',
      vehicle: null,
      disabled: {
        from: new Date()
      },
      form: new window.Form({
        reference_number: null,
        registration: null,
        status_id: null,
        hire_site_id: null,
        group_id: null,
        make_id: null,
        type_id: null,
        driver_id: null,
        model: null,
        vin: null,
        transmission_type_id: null,
        registered_at: null,
        colour: null,
        engine_capacity: null,
        tax_expires_at: null,
        mot_expires_at: null,
        lease_start_date: null,
        lease_end_date: null,
        cost_per_month: 0,
        p11d_value: 0,
        comments: null,
        extras: [],
        defleet_reason_id: null,
        total_loss_category: null,
        reason: null,
        removed_from_dart_congestion: null,
        removed_from_mid: null,
        v5: null,
        spare_key: null,
        sorn: null,
        un_recovered_stolen: null,
        removed_from_dart_congestion_at: null,
        sorn_at: null,
        defleet_arranged_at: null,
        collection_date: null,
        off_mid_date: null,
        customer: null,
        sale_price: null,
        sale_date: null,
        payment_date: null,
        invoice_number: null,
        v5_processed: null,
        collected_at: null
      }),
      data_entered: false,
      showDriver: false
    };
  },

  computed: {
    user() {
      return this.$store.getters.user;
    },

    groups() {
      return this.$store.getters['VehicleGroups/all'];
    },

    types() {
      return this.$store.getters['VehicleTypes/all'];
    },

    drivers() {
      return this.$store.getters['ClaimHandlers/drivers'];
    },

    makes() {
      return this.$store.getters['VehicleMakes/all'];
    },

    transmissions() {
      return this.$store.getters['VehicleTransmissions/all'];
    },

    statuses() {
      return this.$store.getters['VehicleStatuses/all'];
    },

    hireSites() {
      return this.$store.getters['HireSites/all'];
    },

    extras() {
      return this.$store.getters['VehicleExtras/all'];
    },

    loading() {
      return this.identifier && !this.vehicle;
    },

    defleetReasons() {
      return this.$store.getters['DefleetReasons/all'];
    },

    pageTitle() {
      if (this.identifier) {
        return 'Update Vehicle';
      }

      return 'Create Vehicle';
    },

    age() {
      if (!this.form.registered_at) {
        return 'Please select a date';
      }

      let yearsOld = window.DateManager.differenceInYears(
        new Date(),
        this.form.registered_at
      ).toString();
      let split = yearsOld.split('.');
      let ageInYears = split[0];

      let monthsCalc = '0.' + split[1];
      let ageInMonths = Math.floor(
        parseFloat(parseFloat(12) * parseFloat(monthsCalc))
      );

      return ageInYears + ' Years ' + ageInMonths + ' Months';
    }
  },

  mounted() {
    this.$store.commit('VehicleStatuses/retrieve');
    this.$store.commit('HireSites/retrieve');
    this.$store.commit('VehicleExtras/retrieve');
    this.$store.commit('DefleetReasons/retrieve');
    this.$store.commit('ClaimHandlers/retrieve');

    if (this.identifier) {
      this.loadVehicle();
    }
  },

  methods: {
    setRegistration(registration) {
      this.form.registration = registration;
    },

    setVehicle(vehicle) {
      this.form.transmission_type_id = vehicle.transmission_type_id;
      this.form.group_id = vehicle.group_id;
      this.form.type_id = vehicle.type_id;
      // this.form.driver_id = vehicle.driver_id;
      this.form.make_id = vehicle.make_id;
      this.form.model = vehicle.model;
      this.form.vin = vehicle.vin;
      this.form.registered_at = vehicle.registered_at;
      this.form.colour = vehicle.colour;
      this.form.engine_capacity = vehicle.engine_capacity;

      if (vehicle.tax_expires_at) {
        this.form.tax_expires_at = vehicle.tax_expires_at;
      }

      if (vehicle.mot_expires_at) {
        this.form.mot_expires_at = vehicle.mot_expires_at;
      }

      this.form.extras = vehicle.extras?.map(extra => extra.id) || [];
    },

    setRegistrationDate(value) {
      this.form.registered_at = value;
    },

    setTaxExpiry(value) {
      this.form.tax_expires_at = value;
    },

    setMOTExpiry(value) {
      this.form.mot_expires_at = value;
    },

    setLeaseStartDate(value) {
      this.form.lease_start_date = value;
    },

    setLeaseEndDate(value) {
      this.form.lease_end_date = value;
    },

    isDefleeted() {
      let status = this.statuses.find(
        status => status.id === this.form.status_id
      );
      return status?.name === 'defleeted';
    },

    isTheft() {
      let reason = this.defleetReasons.find(
        reasons => reasons.id === this.form.defleet_reason_id
      );
      return reason?.name === 'theft';
    },

    setType(type) {
      this.showDriver = false;
      this.form.type_id = type;

      if (this.isVehicleType('van')) {
        this.showDriver = true;
      }
    },

    isVehicleType(typeName) {
      let vehicleType = this.types.find(type => type.id === this.form.type_id);
      return vehicleType?.name === typeName;
    },

    submit() {
      window.toastify.info('Saving', "We're saving the vehicle...");

      if (!this.isDefleeted()) {
        this.removeDefleet();
      }

      let identifier = this.$store.getters.user.tenant.identifier;

      if (this.identifier) {
        this.form
          .put(`/fleet/${identifier}/${this.identifier}`)
          .then(response => {
            window.toastify.success('Saved', "We've saved the vehicle!");
          })
          .catch(response => {
            window.toastify.error(
              'Error',
              'Sorry, there was an error saving the vehicle.'
            );
          });
      } else {
        this.form
          .post(`/fleet/${identifier}`)
          .then(response => {
            window.toastify.success('Saved', "We've saved the vehicle!");
            this.route('/fleet');
          })
          .catch(response => {
            window.toastify.error(
              'Error',
              'Sorry, there was an error saving the vehicle.'
            );
          });
      }
    },

    loadVehicle() {
      if (this.identifier && !this.vehicle && this.user?.tenant?.identifier) {
        window.Ajax.get(
          `/fleet/${this.user?.tenant?.identifier}/${this.identifier}`
        )
          .then(response => {
            this.setVehicle(response.data);
            this.setType(response.data.type?.id);

            this.vehicle = response.data;
            this.form.group_id = response.data.group?.id;
            this.form.driver_id = response.data.driver?.id;
            this.form.status_id = response.data.status_id;
            this.form.make_id = response.data.make?.id;
            this.form.transmission_type_id = response.data.transmission?.id;
            this.form.hire_site_id = response.data.hire_site_id;
            this.form.registration = response.data.registration;
            this.form.reference_number = response.data.reference_number;
            this.form.cost_per_month = response.data.cost_per_month;
            this.form.p11d_value = response.data.p11d_value;
            this.form.comments = response.data.comments;
            this.form.lease_start_date = response.data.lease_start_date;
            this.form.lease_end_date = response.data.lease_end_date;
            this.form.defleet_reason_id = response.data.defleet_reason_id;
            this.form.total_loss_category = response.data.total_loss_category;
            this.form.reason = response.data.reason;
            this.form.removed_from_dart_congestion =
              response.data.removed_from_dart_congestion;
            this.form.removed_from_mid = response.data.removed_from_mid;
            this.form.v5 = response.data.v5;
            this.form.spare_key = response.data.spare_key;
            this.form.sorn = response.data.sorn;
            this.form.un_recovered_stolen = response.data.un_recovered_stolen;
            this.form.removed_from_dart_congestion_at =
              response.data.removed_from_dart_congestion_at;
            this.form.sorn_at = response.data.sorn_at;
            this.form.defleet_arranged_at = response.data.defleet_arranged_at;
            this.form.collection_date = response.data.collection_date;
            this.form.off_mid_date = response.data.off_mid_date;
            this.form.customer = response.data.customer;
            this.form.sale_price = response.data.sale_price;
            this.form.sale_date = response.data.sale_date;
            this.form.payment_date = response.data.payment_date;
            this.form.invoice_number = response.data.invoice_number;
            this.form.v5_processed = response.data.v5_processed;
            this.form.collected_at = response.data.collected_at;
          })
          .catch(error => {});
      }
    },

    removeDefleet() {
      this.form.defleet_reason_id = null;
    }
  },

  watch: {
    user() {
      this.loadVehicle();
    }
  }
};
</script>
